import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { FiPhone, FiMail } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { Clock, Edit3, ChevronDown, X, Reply } from "lucide-react";
import { useLocation } from "react-router-dom";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";

const CRMMessage = () => {
    const [activeTab, setActiveTab] = useState("Messages");
    const [note, setNote] = useState("");
    const [notesList, setNotesList] = useState([]);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [showNoteBox, setShowNoteBox] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [dateOption, setDateOption] = useState("today"); // 'today'|'tomorrow'|'pick'
    const [timeChoice, setTimeChoice] = useState(null); // 'in30'|'in2h'|'morning'|'evening'|'pick'|null
    const [pickedDate, setPickedDate] = useState(""); // yyyy-mm-dd
    const [pickedTime, setPickedTime] = useState(""); // HH:MM (24h)
    const [saving, setSaving] = useState(false);
    const [isEditingReminder, setIsEditingReminder] = useState(false);
    const [reminderData, setReminderData] = useState(null);
    const [reminderDescription, setReminderDescription] = useState("");
    const [data, setData] = useState([]);
    const { state } = useLocation();
    const mobile = state?.mobile;
    const email = state?.email;
    const name = state?.name;
    const address = state?.address;
    const city = state?.city;
    const pincode = state?.pincode;
    const source = state?.source;
    const extra = state?.extra;
    const AssignLeads = state?.AssignLeads;
    const leadId = state?.leadId;
    const lead = state?.lead;
    console.log(state)
    const url = API_BASE_URL
    const token = decryptToken("accessToken") || ""


    const fetchLeadData = async () => {
        try {
            const response = await fetch(
                `${url}/leads/getallsingle?mobile=${mobile}&email=${email}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const res = await response.json();
            setData(res);
        } catch (error) {
            console.error(error);
        }
    };

    const openReminderModal = (lead) => {
        setIsEditingReminder(false);

        if (lead.remainder_date && lead.remainder_time) {
            setIsEditingReminder(true);
            setReminderData(lead);

            const today = new Date().toISOString().split("T")[0];
            const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];

            // DATE PREFILL
            if (lead.remainder_date === today) {
                setDateOption("today");
            } else if (lead.remainder_date === tomorrow) {
                setDateOption("tomorrow");
            } else {
                setDateOption("pick");
                setPickedDate(lead.remainder_date);
            }

            // TIME PREFILL
            if (lead.remainder_time === "09:00") setTimeChoice("morning");
            else if (lead.remainder_time === "18:00") setTimeChoice("evening");
            else {
                setTimeChoice("pick");
                setPickedTime(lead.remainder_time);
            }

            // ⭐⭐ DESCRIPTION PREFILL
            setReminderDescription(lead.followup_description || "");

        } else {
            // NEW REMINDER
            setIsEditingReminder(false);
            setDateOption("today");
            setTimeChoice(null);
            setPickedDate("");
            setPickedTime("");
            setReminderDescription(""); // RESET
        }

        setModalOpen(true);
    };



    const fetchNoteData = async () => {
        try {
            const response = await fetch(
                `${url}/notec/crmnotes/${leadId}?mobile=${mobile}&email=${email}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const res = await response.json();
            console.log(res);
            setNotesList(res);
        } catch (error) {
            console.error(error);
        }
    };



    const handleStatusChange = async (newStatus, lead) => {
        try {
            const response = await fetch(`${url}/leads/change/${lead.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ extra: newStatus }), // send only the updated field
            });

            const result = await response.json();

            if (response.ok) {
                if (result.warning) {
                    toast.warning(result.warning);
                } else {
                    toast.success(result.message || "Status updated successfully");
                }

                setData((prev) =>
                    prev.map((item) =>
                        item.id === lead.id ? { ...item, extra: newStatus } : item
                    )
                );
            } else {
                toast.error(result.message || "Failed to update status");
            }
        } catch (err) {
            console.error("Error updating status:", err);
            toast.error("An error occurred while updating status");
        }
    };

    useEffect(() => {
        fetchLeadData();
        fetchNoteData();
    }, [])

    const handleSendNote = () => {
        if (!note.trim()) return;

        if (editingNoteId !== null) {
            // update existing note
            setNotesList((prev) =>
                prev.map((n) =>
                    n.id === editingNoteId ? { ...n, text: note } : n
                )
            );
            setEditingNoteId(null);
        } else {
            // add new note
            setNotesList((prev) => [
                ...prev,
                {
                    id: Date.now(),
                    text: note,
                    time: new Date().toLocaleString(),
                },
            ]);
        }

        setNote(""); // clear textarea
    };

    // Prevent background scroll when modal open
    useEffect(() => {
        if (modalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [modalOpen]);

    // Reset time choice when date option changes
    useEffect(() => {
        setTimeChoice(null);
        setPickedTime("");
        if (dateOption !== "pick") {
            setPickedDate("");
        }
    }, [dateOption]);

    // Format readable date
    const formatReadable = (dateObj) => {
        if (!dateObj) return "";
        return dateObj.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };


    // Compute final selected Date object
    const computeSelectedDateTime = () => {
        const now = new Date();

        if (dateOption === "today") {
            if (timeChoice === "in30") {
                return new Date(now.getTime() + 30 * 60 * 1000);
            }
            if (timeChoice === "in2h") {
                return new Date(now.getTime() + 2 * 60 * 60 * 1000);
            }
            if (timeChoice === "pick" && pickedTime) {
                const [hh, mm] = pickedTime.split(":").map(Number);
                const d = new Date();
                d.setHours(hh, mm, 0, 0);
                return d;
            }
        } else if (dateOption === "tomorrow") {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (timeChoice === "morning") {
                tomorrow.setHours(9, 0, 0, 0);
                return tomorrow;
            }
            if (timeChoice === "evening") {
                tomorrow.setHours(18, 0, 0, 0);
                return tomorrow;
            }
            if (timeChoice === "pick" && pickedTime) {
                const [hh, mm] = pickedTime.split(":").map(Number);
                tomorrow.setHours(hh, mm, 0, 0);
                return tomorrow;
            }
        } else if (dateOption === "pick" && pickedDate) {
            const [y, m, d] = pickedDate.split("-").map(Number);
            const base = new Date(y, m - 1, d);

            if (timeChoice === "morning") {
                base.setHours(9, 0, 0, 0);
                return base;
            }
            if (timeChoice === "evening") {
                base.setHours(18, 0, 0, 0);
                return base;
            }
            if (timeChoice === "pick" && pickedTime) {
                const [hh, mm] = pickedTime.split(":").map(Number);
                base.setHours(hh, mm, 0, 0);
                return base;
            }
            // Default: midnight
            return base;
        }

        return null;
    };

    const selectedDateTime = computeSelectedDateTime();

    // Updated canSave logic
    const canSave =
        (dateOption === "today" && (
            timeChoice === "in30" ||
            timeChoice === "in2h" ||
            (timeChoice === "pick" && pickedTime)
        )) ||
        (dateOption === "tomorrow" && (
            timeChoice === "morning" ||
            timeChoice === "evening" ||
            (timeChoice === "pick" && pickedTime)
        )) ||
        (dateOption === "pick" && pickedDate && (
            timeChoice === "morning" ||
            timeChoice === "evening" ||
            (timeChoice === "pick" && pickedTime)
        ));

    const handleSave = async () => {
        if (!canSave) return;

        setSaving(true);

        const finalISO = selectedDateTime.toISOString();
        const finalDate = finalISO.split("T")[0];
        const finalTime = finalISO.split("T")[1].slice(0, 5);

        const payload = {
            remainder_date: finalDate,
            remainder_time: finalTime,
            followup_description: reminderDescription,  // ⭐ SAVE DESCRIPTION
            followup_status: "Pending"
        };

        try {
            await fetch(`${url}/leads/change/${leadId}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            toast.success(isEditingReminder ? "Reminder updated!" : "Reminder added!");

            setReminderData(payload);

        } catch (err) {
            toast.error("Failed to save reminder");
        }

        setSaving(false);
        setModalOpen(false);
        setIsEditingReminder(false);
    };



    return (
        <>
            {/* Main card */}
            <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100">
                {/* TOP SECTION (Two Columns) */}
                <div className="flex justify-between">
                    {/* LEFT SIDE CONTENT */}
                    <div className="w-[80%]">
                        <p className="text-gray-400 text-xs mb-2">12 Sept 2025 • 02:10:53 PM</p>

                        <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">{name}</h2>
                            <span className="bg-blue-50 text-blue-600 px-2.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide">
                                Business
                            </span>
                        </div>

                        <div className="space-y-1 text-sm text-gray-700">
                            {AssignLeads && AssignLeads.length > 0 ? (
                                <p>
                                    <span className="font-medium text-gray-900">Assigned to:</span>{" "}
                                    {AssignLeads.map((assign, index) => (
                                        <span key={assign.id}>
                                            {assign.staff_name.replace(/\(ID:.*?\)/, "").trim()}
                                            {index < AssignLeads.length - 1 ? ", " : ""}
                                        </span>
                                    ))}
                                </p>
                            ) : null}
                            <p>
                                <span className="font-medium text-gray-900">Source:</span> {source}
                            </p>
                            <p>
                                <span className="font-medium text-gray-900">Address:</span>
                                <br />
                                {address} {city} {pincode}
                            </p>
                        </div>
                    </div>

                    {/* RIGHT ICONS */}
                    <div className="flex flex-col items-center gap-3">
                        <div
                            onClick={() => {
                                if (mobile) window.location.href = `tel:${mobile}`;
                            }}
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700 cursor-pointer transition-all hover:from-blue-100 hover:to-blue-200 hover:text-blue-900">
                            <FiPhone />
                        </div>

                        <div
                            onClick={() => {
                                if (email) window.location.href = `mailto:${email}`;
                            }}
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm bg-gradient-to-br from-yellow-50 to-yellow-100 text-yellow-700 cursor-pointer transition-all hover:from-yellow-100 hover:to-yellow-200 hover:text-yellow-900">
                            <FiMail />
                        </div>

                        <div
                            onClick={() => {
                                if (mobile) {
                                    const phone = mobile.startsWith("+") ? mobile : `+91${mobile}`;
                                    window.open(`https://wa.me/${phone}`, "_blank");
                                }
                            }}
                            className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm bg-gradient-to-br from-green-50 to-green-100 text-green-700 cursor-pointer transition-all hover:from-green-100 hover:to-green-200 hover:text-green-900">
                            <FaWhatsapp />
                        </div>
                    </div>
                </div>

                {/* BOTTOM BUTTONS */}
                <div className="flex items-center gap-2 mt-5">
                    {/* Set Reminder */}
                    <button
                        onClick={() => openReminderModal(lead)}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 transition cursor-pointer h-8"
                    >
                        <Clock size={16} />
                        Set Reminder
                    </button>

                    {/* Dropdown */}
                    <div className="relative h-8">
                        <select
                            name="extra"
                            value={extra || ""}
                            onChange={(e) => handleStatusChange(e.target.value, lead)}
                            className="appearance-none px-3 py-1 text-sm h-full bg-gray-100 text-gray-700 rounded-lg border border-gray-300 pr-6 cursor-pointer hover:bg-gray-200 transition">
                            <option value="">Select</option>
                            <option value="Pending">Pending</option>
                            <option value="Converted">Converted</option>
                            <option value="Not Interested">Not Interested</option>
                            <option value="In Progress">In Progress</option>
                        </select>
                        <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-700 pointer-events-none" />
                    </div>
                </div>
            </div>

            {/* Tabs + content */}
            <div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100 mt-4">
                <div className="flex gap-3">
                    {["Messages", "Notes", "Activities"].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeTab === tab ? "bg-blue-600 text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="mt-4">
                    {activeTab === "Messages" && (
                        <div className="space-y-2">
                            {data.length === 0 ? (
                                <p className="text-gray-500 text-sm">No messages found.</p>
                            ) : (
                                data.map((msg, index) => (
                                    <div
                                        key={index}
                                        className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm"
                                    >
                                        <p className="text-xs text-gray-400 mb-1">
                                            {formatDate(msg.createdAt)}
                                        </p>

                                        <p className="text-sm text-gray-800">
                                            {msg.remark || "No remark"}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}


                    {activeTab === "Notes" && (
                        <div className="space-y-2">
                            {notesList.length === 0 ? (
                                <p className="text-gray-500 text-sm">No notes found.</p>
                            ) : (
                                notesList.map((msg, index) => (
                                    <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                        <p className="text-xs text-gray-400 mb-1">  {formatDate(msg.createdAt)}</p>
                                        <p className="text-sm text-gray-800"> {msg.remark || "No note"}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === "Activities" && (
                        <div className="space-y-2">
                            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                {/* <p className="text-xs text-gray-400 mb-1">14 Sept 2025 • 03:30 PM</p> */}
                                {lead?.remainder_date ? (
                                    <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                        <p className="text-xs text-gray-400 mb-1">
                                            {formatDate(`${lead.remainder_date} ${lead.remainder_time}`)}
                                        </p>
                                        <p className="text-sm text-gray-800">
                                            Reminder set for <strong>{formatDate(`${lead.remainder_date} ${lead.remainder_time}`)}</strong>
                                        </p>

                                        <button
                                            className="text-blue-600 mt-2 text-sm underline"
                                            onClick={() => openReminderModal(lead)}
                                        >
                                            Edit Reminder
                                        </button>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">No reminder set.</p>
                                )}

                                <div
                                    className="flex justify-end items-center gap-2 text-blue-500 hover:text-blue-600 cursor-pointer"
                                    onClick={() => {
                                        setShowNoteBox(true);
                                        setEditingNoteId(null);
                                        setNote("");
                                    }}
                                >
                                    <span className="capitalize text-sm font-semibold">write note</span>
                                    <span><Reply size={18} /></span>
                                </div>

                                {showNoteBox && (
                                    <div className="flex flex-col gap-1 w-full mt-2">

                                        <label htmlFor="notes" className="text-sm font-medium text-gray-700">
                                            {editingNoteId !== null ? "Edit Note" : "Add a Note"}
                                        </label>

                                        <textarea
                                            id="notes"
                                            name="notes"
                                            rows={1}
                                            value={note}
                                            onChange={(e) => {
                                                setNote(e.target.value);

                                                // Auto-expand (max 5 rows)
                                                const textarea = e.target;
                                                textarea.style.height = "auto";
                                                const maxHeight = 5 * 24; // ~24px per row
                                                textarea.style.height =
                                                    textarea.scrollHeight > maxHeight
                                                        ? maxHeight + "px"
                                                        : textarea.scrollHeight + "px";
                                            }}
                                            onKeyDown={(e) => {
                                                // SHIFT + ENTER → new line
                                                if (e.key === "Enter" && e.shiftKey) return;

                                                // ENTER → send note
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleSendNote();
                                                }
                                            }}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none overflow-y-auto"
                                            placeholder="Write a note..."
                                            style={{
                                                maxHeight: "120px", // caps at 5 rows
                                            }}
                                        ></textarea>

                                        <div className="flex justify-end gap-2 mt-1">
                                            <button
                                                onClick={() => {
                                                    setShowNoteBox(false);
                                                    setEditingNoteId(null);
                                                    setNote("");
                                                }}
                                                className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition"
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                onClick={() => {
                                                    handleSendNote();
                                                    setShowNoteBox(false);
                                                }}
                                                className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                                            >
                                                {editingNoteId !== null ? "Update" : "Send"}
                                            </button>
                                        </div>

                                        {/* Notes list */}
                                        <div className="mt-3 space-y-2">
                                            {notesList.map((n) => (
                                                <div
                                                    key={n.id}
                                                    className="p-3 bg-white border rounded-lg shadow-sm flex justify-between items-start"
                                                >
                                                    <div>
                                                        <p className="text-sm text-gray-800 whitespace-pre-line">{n.text}</p>
                                                        <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                                                    </div>

                                                    <button
                                                        className="text-blue-600 text-xs"
                                                        onClick={() => {
                                                            setEditingNoteId(n.id);
                                                            setNote(n.text);
                                                            setShowNoteBox(true);
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                    </div>
                                )}


                            </div>

                            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                <p className="text-xs text-gray-400 mb-1">14 Sept 2025 • 02:45 PM</p>
                                <p className="text-sm text-gray-800">
                                    Called customer via <strong>phone</strong>.
                                </p>
                            </div>

                            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                <p className="text-xs text-gray-400 mb-1">14 Sept 2025 • 02:40 PM</p>
                                <p className="text-sm text-gray-800">
                                    Sent <strong>WhatsApp</strong> message to customer.
                                </p>
                            </div>

                            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                <p className="text-xs text-gray-400 mb-1">14 Sept 2025 • 02:30 PM</p>
                                <p className="text-sm text-gray-800">
                                    Sent <strong>Email</strong> to customer.
                                </p>
                            </div>

                            <div className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                                <p className="text-xs text-gray-400 mb-1">14 Sept 2025 • 02:20 PM</p>
                                <p className="text-sm text-gray-800">
                                    Status changed to <strong>Converted</strong>.
                                </p>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Modal (Bottom Sheet) */}
            {modalOpen && (
                <>
                    <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setModalOpen(false)} />
                    <div className="fixed bottom-0 left-0 right-0 z-[70]">
                        <div className="mx-auto max-w-3xl">
                            <div className="bg-white rounded-t-2xl shadow-lg border border-gray-100 p-4">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-sm font-semibold">Set Reminder</h3>
                                    </div>
                                    <button onClick={() => setModalOpen(false)} className="p-2 rounded-md hover:bg-gray-100">
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Date Selection */}
                                <div className="mb-3">
                                    <p className="text-xs text-gray-500 mb-2">Select date</p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setDateOption("today")}
                                            className={`px-3 py-1 rounded-lg text-sm border ${dateOption === "today" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                        >
                                            Today
                                        </button>
                                        <button
                                            onClick={() => setDateOption("tomorrow")}
                                            className={`px-3 py-1 rounded-lg text-sm border ${dateOption === "tomorrow" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                        >
                                            Tomorrow
                                        </button>
                                        <button
                                            onClick={() => setDateOption("pick")}
                                            className={`px-3 py-1 rounded-lg text-sm border ${dateOption === "pick" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                        >
                                            Pick a date
                                        </button>
                                    </div>
                                </div>

                                {/* Custom Date Picker */}
                                {dateOption === "pick" && (
                                    <div className="mb-3">
                                        <p className="text-xs text-gray-500 mb-2">Choose a date</p>
                                        <input
                                            type="date"
                                            value={pickedDate}
                                            onChange={(e) => setPickedDate(e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm"
                                        />
                                        {pickedDate && (
                                            <p className="text-xs text-gray-400 mt-2">Selected: {new Date(pickedDate).toDateString()}</p>
                                        )}
                                    </div>
                                )}

                                {/* Time Selection */}
                                <div className="mb-3">
                                    <p className="text-xs text-gray-500 mb-2">Select time</p>

                                    {/* Always show quick options based on date, but keep them visible after selection */}
                                    {(dateOption === "today" || dateOption === "tomorrow" || (dateOption === "pick" && pickedDate)) && (
                                        <div className="flex gap-2 flex-wrap mb-3">
                                            {/* Today Quick Options */}
                                            {dateOption === "today" && (
                                                <>
                                                    <button
                                                        onClick={() => setTimeChoice("in30")}
                                                        className={`px-3 py-1 rounded-lg text-sm border ${timeChoice === "in30" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                                    >
                                                        In 30 mins
                                                    </button>
                                                    <button
                                                        onClick={() => setTimeChoice("in2h")}
                                                        className={`px-3 py-1 rounded-lg text-sm border ${timeChoice === "in2h" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                                    >
                                                        In 2 hours
                                                    </button>
                                                </>
                                            )}

                                            {/* Tomorrow or Custom Date Quick Options */}
                                            {(dateOption === "tomorrow" || (dateOption === "pick" && pickedDate)) && (
                                                <>
                                                    <button
                                                        onClick={() => setTimeChoice("morning")}
                                                        className={`px-3 py-1 rounded-lg text-sm border ${timeChoice === "morning" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                                    >
                                                        Morning (09:00)
                                                    </button>
                                                    <button
                                                        onClick={() => setTimeChoice("evening")}
                                                        className={`px-3 py-1 rounded-lg text-sm border ${timeChoice === "evening" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                                    >
                                                        Evening (18:00)
                                                    </button>
                                                </>
                                            )}

                                            {/* Always show "Pick a time" button */}
                                            <button
                                                onClick={() => setTimeChoice("pick")}
                                                className={`px-3 py-1 rounded-lg text-sm border ${timeChoice === "pick" ? "bg-blue-600 text-white border-transparent" : "bg-gray-100 text-gray-700 border-gray-200"}`}
                                            >
                                                Pick a time
                                            </button>
                                        </div>
                                    )}

                                    {/* Custom Time Picker - only show when "Pick a time" is selected */}
                                    {timeChoice === "pick" && (
                                        <div className="mt-2">
                                            <input
                                                type="time"
                                                value={pickedTime}
                                                onChange={(e) => setPickedTime(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm"
                                            />
                                            {pickedTime && (
                                                <p className="text-xs text-gray-400 mt-1">Chosen time: {pickedTime}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Optional: Show selected quick time summary */}
                                    {timeChoice && timeChoice !== "pick" && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            Selected: <span className="font-medium text-gray-700">
                                                {timeChoice === "in30" && "In 30 minutes"}
                                                {timeChoice === "in2h" && "In 2 hours"}
                                                {timeChoice === "morning" && "Morning (09:00)"}
                                                {timeChoice === "evening" && "Evening (18:00)"}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Preview */}
                                <div className="mb-3 pt-3 border-t border-gray-200">
                                    <p className="text-xs text-gray-500 mb-2">Preview</p>
                                    <div className=" text-sm text-gray-700">
                                        {selectedDateTime ? (
                                            <>
                                                <div className="font-medium">Reminder set for</div>
                                                <div className="text-sm text-gray-500">{formatReadable(selectedDateTime)}</div>
                                            </>
                                        ) : (
                                            <div className="text-gray-500">No time selected yet</div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-end gap-2">
                                    <button
                                        onClick={() => setModalOpen(false)}
                                        className="px-3 py-2 rounded-lg bg-gray-100 text-sm text-gray-700 border border-gray-200 hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={!canSave || saving}
                                        className={`px-4 py-2 rounded-lg text-sm text-white ${canSave && !saving ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-300 cursor-not-allowed"}`}
                                    >
                                        {saving ? "Saving..." : "Save Reminder"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default CRMMessage;